import { Injectable } from '@angular/core';
import { GraphData, ORGANISATION_MODEL_KEY } from '../common/linqset-model';
import { MODEL_API, RepositoryService } from '../linq-utils/repository.service';
import { InsightsRefreshService } from './insights-refresh.service';
import { InsightsService } from './insights.service';

const COMPONENT = 'Organisation model reader';

// how long a read is shared once it has finished: long enough for the views of one screen loading together, short enough
// that a later visit reads afresh
const SHARE_FOR_MS = 5000;

// Reads the organisation model (its roles and systems, with their insights) for a workspace once for the views on one
// screen that need it together, such as the home screen's overview and its information assets, rather than once each
@Injectable({ providedIn: 'root' })
export class OrganisationModelReader {
    private reads = new Map<string, { promise: Promise<GraphData>; settled?: number }>();

    constructor(private repoService: RepositoryService, private insightsService: InsightsService, private refreshes: InsightsRefreshService) {}

    read(organisationKey: string, workspaceKey: string): Promise<GraphData> {
        const key = `${organisationKey}|${workspaceKey}`;
        const shared = this.reads.get(key);
        // insights being worked out again would change it, so it is read again once they're ready
        if (shared && !this.refreshes.isRefreshing(workspaceKey) && (shared.settled === undefined || Date.now() - shared.settled < SHARE_FOR_MS)) return shared.promise;

        const read = {
            promise: this.insightsService.afterRefresh(workspaceKey, () =>
                this.repoService.callSingleAPI(this.repoService.getData<GraphData>(MODEL_API(organisationKey, ORGANISATION_MODEL_KEY, workspaceKey)), COMPONENT, 'Load the organisation model')),
        } as { promise: Promise<GraphData>; settled?: number };
        read.promise.then(() => read.settled = Date.now(), () => this.reads.delete(key));
        this.reads.set(key, read);
        return read.promise;
    }
}
